/**
 * ClubNotificationButton - Subscribe to notifications from a specific club
 */
import React, { useState, useEffect, useCallback } from 'react';
import { Bell, BellOff, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { safeFetch } from '../utils/safeFetch';
import { isPushSupported } from '../hooks/usePushNotifications';
import { API_URL } from '../utils/api';

export function ClubNotificationButton({ clubId, clubName, variant = "secondary", size = "default", showLabel = true }) {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [hasGlobalSubscription, setHasGlobalSubscription] = useState(false);

  const checkSubscriptionStatus = useCallback(async () => {
    if (!isPushSupported()) {
      setIsLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      const response = await safeFetch(`${API_URL}/api/push/status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.data) {
        setHasGlobalSubscription(response.data.subscribed);
        const subscribedClubs = response.data.subscription_details?.subscribed_clubs || [];
        setIsSubscribed(subscribedClubs.includes(clubId));
      }
    } catch (err) {
      console.error('Error checking club subscription:', err);
    } finally {
      setIsLoading(false);
    }
  }, [clubId]);

  useEffect(() => {
    checkSubscriptionStatus();
  }, [checkSubscriptionStatus]);

  const toggleClubSubscription = async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      toast.error('Please log in to receive notifications');
      return;
    }

    if (!hasGlobalSubscription) {
      toast.info('Enable notifications first in your Dashboard → Account settings');
      return;
    }

    setIsLoading(true);
    try {
      const endpoint = isSubscribed 
        ? `${API_URL}/api/push/unsubscribe-from-club/${clubId}`
        : `${API_URL}/api/push/subscribe-to-club/${clubId}`;

      const response = await safeFetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data?.success) {
        setIsSubscribed(!isSubscribed);
        toast.success(isSubscribed 
          ? `Unsubscribed from ${clubName} notifications`
          : `You'll now receive notifications from ${clubName}`
        );
      } else {
        toast.error(response.data?.detail || 'Failed to update subscription');
      }
    } catch (err) {
      toast.error(err.message || 'Failed to update subscription');
    } finally {
      setIsLoading(false);
    }
  };

  // Don't show if push not supported
  if (!isPushSupported()) {
    return null;
  }

  // Don't show if not logged in
  const token = localStorage.getItem('token');
  if (!token) {
    return null;
  }

  return (
    <Button
      variant={variant}
      size={size}
      onClick={toggleClubSubscription}
      disabled={isLoading}
      className={`${isSubscribed ? 'text-violet-600 border-violet-300' : 'text-slate-700'} ${variant === 'secondary' ? 'bg-white shadow-sm border border-slate-200 hover:bg-slate-50' : ''}`}
      data-testid="club-notification-btn"
      title={isSubscribed ? `Stop notifications from ${clubName}` : `Get notified about ${clubName} events`}
    >
      {isLoading ? (
        <Loader2 className="w-5 h-5 animate-spin" />
      ) : isSubscribed ? (
        <Bell className="w-5 h-5 fill-violet-500 text-violet-600" />
      ) : (
        <BellOff className="w-5 h-5" />
      )}
      {showLabel && (
        <span className="ml-2 hidden sm:inline">
          {isSubscribed ? 'Notifications On' : 'Get Notified'}
        </span>
      )}
    </Button>
  );
}

export default ClubNotificationButton;
